import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MatLegacyDialogModule, MatLegacyDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacyTabsModule } from '@angular/material/legacy-tabs';
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  combineLatest,
  debounceTime,
  first,
  map,
  of,
  startWith,
  switchMap,
  takeUntil,
  withLatestFrom,
} from 'rxjs';

import { AutofocusDirective, ButtonComponent, IconComponent, trackByFactory } from '@mode/capra';
import { DialogHeaderComponent, LoaderComponent, StatusBannerComponent } from '@mode/shared/ui';
import { shareReplayDefault, HelpDocsService } from '@mode/shared/util-js';
import { AccountFacade } from '@mode/accounts/contract';
import { AccountConnectionCtaComponent } from './account-connection-cta/account-connection-cta.component';
import { SheetsBrowserComponent } from './sheets-browser/sheets-browser.component';
import {
  Spreadsheet,
  SpreadsheetDto,
  Worksheet,
  WorksheetDto,
  mapDtoToSpreadsheet,
  mapDtoToWorksheet,
} from './data-mappers';
import { UploadSheetForm, UploadSheetFormData } from './upload-sheet-form';

export interface UploadSheetDialogData {
  confirmAction?: (form: UploadSheetFormData) => void;
}

interface ViewModel {
  userConnected: boolean;
  email: string;
  accountUrl: string;
  spreadsheets: readonly Readonly<Spreadsheet>[];
  worksheets: readonly Readonly<Worksheet>[];
  showLoadMore: boolean;
  spreadsheetName: string;
}

interface ApiResponse {
  _embedded: Readonly<{ spreadsheets: readonly SpreadsheetDto[] }>;
  _links?: Readonly<{
    next_page?: Readonly<{ href: string }>;
  }>;
}

interface SpreadsheetPage {
  spreadsheets: readonly Spreadsheet[];
  nextPage?: string;
}

const TABS = ['Search', 'By URL'] as const;
export type Tab = (typeof TABS)[number];

@Component({
  standalone: true,
  selector: 'mode-upload-sheet-dialog',
  imports: [
    AccountConnectionCtaComponent,
    AutofocusDirective,
    ButtonComponent,
    CommonModule,
    DialogHeaderComponent,
    FormsModule,
    IconComponent,
    MatLegacyDialogModule,
    MatLegacyTabsModule,
    LoaderComponent,
    ReactiveFormsModule,
    SheetsBrowserComponent,
    StatusBannerComponent,
  ],
  templateUrl: './upload-sheet-dialog.component.html',
  styleUrls: ['./upload-sheet-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadSheetDialogComponent implements OnInit, OnDestroy {
  @Output() create = new EventEmitter<UploadSheetFormData>();

  readonly headerText = 'Create a Dataset from a Google Sheet';
  readonly helpLink = this.helpDocs.links['https://mode.com/help/articles/google-sheets-integration'];
  readonly tabs = TABS;
  readonly tabTrackByFn = trackByFactory<Tab>((tab) => tab);
  readonly form = new UploadSheetForm();

  private _destroy$ = new Subject<void>();
  private _loadMore$ = new BehaviorSubject(false);
  private _searchText$ = this.form.searchText.valueChanges.pipe(startWith(''), debounceTime(500));
  private _spreadsheetId$ = this.form.spreadsheetId.valueChanges.pipe(startWith(''));
  private _spreadsheetPageSubject$ = new BehaviorSubject<SpreadsheetPage>({ spreadsheets: [] });
  private _spreadsheetNameSubject$ = new BehaviorSubject<string>('');

  private _accountData$ = combineLatest([
    this._accountFacade.user$.pipe(first()),
    this._accountFacade.currentOrg$.pipe(first()),
  ]);
  private _connection$ = this._accountData$.pipe(
    switchMap(([{ token }, { username }]) =>
      this._http.get<{ email_address: string }>(`/api/${username}/users/${token}/google_connection`)
    ),
    map(({ email_address }) => ({
      userConnected: true,
      email: email_address,
    })),
    catchError((err) => {
      return of({
        userConnected: err.status !== 403,
        email: '',
      });
    }),
    shareReplayDefault()
  );

  private _searchRequests$: Observable<SpreadsheetPage> = combineLatest([
    this._accountData$,
    this._connection$,
    this._searchText$,
  ]).pipe(
    switchMap(([[{ token }, { username }], { userConnected }, searchText]) =>
      userConnected
        ? this._http
            .get<ApiResponse>(
              `/api/${username}/users/${token}/google_spreadsheets${searchText ? '?search_text=' + searchText : ''}`
            )
            .pipe(
              catchError(() => of<ApiResponse>({ _embedded: { spreadsheets: [] } })),
              map((response) => ({
                spreadsheets: response._embedded.spreadsheets.map(mapDtoToSpreadsheet),
                nextPage: response._links?.next_page?.href,
              }))
            )
        : of<SpreadsheetPage>({ spreadsheets: [] })
    ),
    shareReplayDefault()
  );
  private _loadRequests$ = this._loadMore$.pipe(
    withLatestFrom(this._spreadsheetPageSubject$),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    switchMap(([_, { spreadsheets, nextPage }]) =>
      nextPage
        ? this._http.get<ApiResponse>(nextPage).pipe(
            catchError(() => of<ApiResponse>({ _embedded: { spreadsheets: [] } })),
            map((response) => ({
              spreadsheets: spreadsheets.concat(response._embedded.spreadsheets.map(mapDtoToSpreadsheet)),
              nextPage: response._links?.next_page?.href,
            }))
          )
        : of<SpreadsheetPage>({ spreadsheets: [] })
    )
  );
  private _worksheets$ = combineLatest([this._accountData$, this._connection$, this._spreadsheetId$]).pipe(
    switchMap(([[{ token }, { username }], { userConnected }, spreadsheetId]) => {
      if (userConnected && spreadsheetId) {
        return this._http.get<{ _embedded: { worksheets: WorksheetDto[] } }>(
          `/api/${username}/users/${token}/google_spreadsheets/${spreadsheetId}/google_worksheets`
        );
      }
      return of<{ _embedded: { worksheets: WorksheetDto[] } }>({ _embedded: { worksheets: [] } });
    }),
    map(({ _embedded: { worksheets } }) => worksheets.map(mapDtoToWorksheet)),
    catchError(() => of<Readonly<Worksheet>[]>([]))
  );

  vm$: Observable<ViewModel> = combineLatest([
    this._accountFacade.currentOrg$.pipe(first()),
    this._connection$,
    this._spreadsheetPageSubject$.asObservable(),
    this._worksheets$,
    this._spreadsheetNameSubject$.pipe(map((name) => name || 'Untitled Dataset')),
    this._searchRequests$.pipe(first()), // Included to prevent vm$ from emitting until the initial HTTP request completes
  ]).pipe(
    map(([{ links }, { userConnected, email }, { spreadsheets, nextPage }, worksheets, spreadsheetName]) => {
      return {
        userConnected,
        email,
        accountUrl: links.googleAccount ?? '',
        spreadsheets,
        worksheets,
        showLoadMore: !!nextPage,
        spreadsheetName,
      };
    })
  );

  constructor(
    public helpDocs: HelpDocsService,
    protected dialogRef: MatLegacyDialogRef<UploadSheetDialogComponent>,
    private _http: HttpClient,
    private _accountFacade: AccountFacade
  ) {}

  ngOnInit() {
    this._searchRequests$
      .pipe(takeUntil(this._destroy$))
      .subscribe({ next: (page) => this._spreadsheetPageSubject$.next(page) });

    this._loadRequests$
      .pipe(takeUntil(this._destroy$))
      .subscribe({ next: (page) => this._spreadsheetPageSubject$.next(page) });

    this.form.tab.valueChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe({ next: () => this._spreadsheetNameSubject$.next('') });
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }

  loadMore() {
    this._loadMore$.next(true);
  }

  onNameBlur() {
    this._spreadsheetNameSubject$.next(this.form.name.value);
  }

  onSubmit() {
    this.create.emit(this.form.getRawValue());
    this.dialogRef.close();
  }
}
